<template>
  <div class="w-full" data-testid="pinning-session-entry">
    <!-- Completed session summary -->
    <div
      v-if="artwork.pinningSessionId"
      class="flex flex-col gap-3 rounded-lg border-2 border-green-400 bg-green-100 p-4"
      data-testid="completed-session-indicator"
    >
      <div class="flex flex-col gap-1">
        <div class="flex items-center gap-2">
          <span class="text-sm font-semibold text-gray-900 sm:text-base">Your Artist Edits</span>
          <div v-click-outside="() => (showInfoTooltip = false)" class="group relative inline-flex">
            <button
              type="button"
              class="inline-flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
              aria-label="Design services information"
              data-testid="design-services-info-tooltip"
              @click="showInfoTooltip = !showInfoTooltip"
            >
              <UseSvg icon="info-circle-thin" class="h-4 w-4 text-gray-500 hover:text-gray-700" />
            </button>
            <div
              :class="[
                'absolute bottom-full right-0 z-50 mb-2 w-max max-w-[250px] rounded-md bg-blue-700 p-3 text-sm text-white shadow-lg transition-opacity',
                showInfoTooltip
                  ? 'pointer-events-auto opacity-100'
                  : 'pointer-events-none opacity-0 group-hover:pointer-events-auto group-hover:opacity-100'
              ]"
            >
              <p class="m-0 text-wrap">
                Our design team will perform the requested changes once the order is completed
              </p>
              <div
                class="absolute right-1.5 top-full border-[6px] border-transparent border-t-blue-700"
              />
            </div>
          </div>
        </div>
        <div
          v-if="artwork.pinningSummary"
          class="text-sm text-gray-700"
          data-testid="markdown-content"
        >
          <div
            class="markdown-rendered text-inherit"
            v-html="renderMarkdown(artwork.pinningSummary)"
          />
        </div>
      </div>
      <div class="flex gap-2 self-end">
        <button
          type="button"
          class="shrink-0 cursor-pointer rounded-[8px] border border-[#c90505] bg-transparent px-3.5 py-2 text-sm font-semibold [font-family:inherit] hover:bg-[rgba(201,5,5,0.06)] disabled:pointer-events-none disabled:opacity-25"
          style="color: #c90505"
          data-testid="cancel-design-request-button"
          @click="handleRemove"
        >
          Cancel
        </button>
        <button
          type="button"
          class="shrink-0 cursor-pointer rounded-[8px] border border-[#414192] bg-[#414192] px-3.5 py-2 text-sm font-semibold !text-white [font-family:inherit] disabled:pointer-events-none disabled:opacity-25"
          data-testid="edit-design-request-button"
          @click="handleEdit"
        >
          Edit
        </button>
      </div>
    </div>

    <!-- New session button -->
    <div v-else class="flex flex-col gap-2">
      <div class="flex items-center gap-2">
        <span class="text-sm font-semibold text-neutral-950">
          How can we make your image better?
        </span>
      </div>
      <button
        type="button"
        class="border-cta-gradient flex w-full cursor-pointer items-center justify-center gap-2 rounded-lg !border-[1px] p-3 text-sm font-medium text-neutral-950 hover:![background-image:linear-gradient(#f3f4f6,#f3f4f6),linear-gradient(103.21deg,#a855f7_6.2%,#2563eb_108.53%)] disabled:cursor-not-allowed disabled:opacity-60"
        data-testid="pinning-entry-button"
        :disabled="isLoading || isEnhancementInProgress"
        @click="handleOpen"
      >
        <SparklesGradientIcon class="h-5 w-5" />
        <span class="font-bold">{{
          isLoading ? "Starting session..." : "Get Free Artist Edits"
        }}</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import SparklesGradientIcon from "assets/icons/sparkles-gradient.svg?vue";
import UseSvg from "common/vue/components/UseSvg.vue";
import { computed, ref } from "vue";
import { useDtfStore } from "../store";
import { useMarkdownRenderer } from "./composables/useMarkdownRenderer";

defineOptions({ name: "PinningSessionEntry" });

const { renderMarkdown } = useMarkdownRenderer();

const props = defineProps({
  index: { type: Number, required: true },
  artwork: { type: Object, required: true }
});

const store = useDtfStore();
const isLoading = computed(() => store.pinningSessionLoading);
const isEnhancementInProgress = computed(() => {
  if (!props.artwork?.id || props.artwork.isSimulated) return true;
  if (props.artwork.isLoading) return true;
  return store.isStandardArtwork && !props.artwork.enhancementCompleted;
});
const showInfoTooltip = ref(false);

function handleOpen() {
  if (isLoading.value || isEnhancementInProgress.value) {
    return;
  }
  store.openPinningSession(props.index);
}

function handleEdit() {
  store.editPinningSession(props.index);
}

function handleRemove() {
  store.clearPinningSession(props.index);
}
</script>

<style scoped>
.markdown-rendered :deep(ul) {
  margin-left: 1rem;
  list-style-type: disc;
}

.markdown-rendered :deep(ul li) {
  list-style-type: disc;
}

.markdown-rendered :deep(ul),
.markdown-rendered :deep(ol) {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.markdown-rendered :deep(ol) {
  margin-left: 1rem;
  list-style-type: decimal;
}

.markdown-rendered :deep(ol li) {
  list-style-type: decimal;
}

.markdown-rendered :deep(p) {
  margin-bottom: 0.5rem;
}

.markdown-rendered :deep(p:last-child) {
  margin-bottom: 0;
}

.markdown-rendered :deep(strong) {
  font-weight: 600;
}

.markdown-rendered :deep(a) {
  color: #2563eb;
  text-decoration: underline;
}

.markdown-rendered :deep(a:hover) {
  color: #1e40af;
}

.markdown-rendered :deep(blockquote) {
  border-left: 4px solid #d1d5db;
  padding-left: 0.75rem;
  font-style: italic;
  color: #4b5563;
}

.markdown-rendered :deep(code) {
  border-radius: 0.25rem;
  background-color: #f3f4f6;
  padding: 0.125rem 0.25rem;
  font-family: ui-monospace, monospace;
  font-size: 0.875rem;
}

.markdown-rendered :deep(pre) {
  overflow-x: auto;
  border-radius: 0.375rem;
  background-color: #f3f4f6;
  padding: 0.5rem;
  font-size: 0.875rem;
}
</style>
